import * as j from "@char/justin";

const strongRef = j.obj({ uri: j.string, cid: j.string });
const blob = j.obj({ ref: j.obj({ $link: j.string }), mimeType: j.string });
const positive = j.custom((value): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0
);
const offset = j.custom((value): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0
);
const aspectRatio = j.optional(j.obj({ width: positive, height: positive }));
const images = j.obj({
  $type: j.literal("app.bsky.embed.images"),
  images: j.array(j.obj({ image: blob, alt: j.string, aspectRatio })),
});
const video = j.obj({
  $type: j.literal("app.bsky.embed.video"),
  video: blob,
  alt: j.optional(j.string),
  aspectRatio,
  captions: j.optional(j.array(j.obj({ lang: j.string, file: blob }))),
});
const external = j.obj({
  $type: j.literal("app.bsky.embed.external"),
  external: j.obj({
    uri: j.string,
    title: j.string,
    description: j.string,
    thumb: j.optional(blob),
  }),
});
const quote = j.obj({
  $type: j.literal("app.bsky.embed.record"),
  record: strongRef,
});
const media = j.union(images, video, external);
const embedSchema = j.union(
  media,
  quote,
  j.obj({
    $type: j.literal("app.bsky.embed.recordWithMedia"),
    record: quote,
    media,
  }),
);
export type Embed = j.Infer<typeof embedSchema>;
const validateEmbed = j.validation.compile(embedSchema);

const facetSchema = j.obj({
  index: j.obj({ byteStart: offset, byteEnd: offset }),
  features: j.array(j.union(
    j.obj({ $type: j.literal("app.bsky.richtext.facet#link"), uri: j.string }),
    j.obj({
      $type: j.literal("app.bsky.richtext.facet#mention"),
      did: j.string,
    }),
    j.obj({ $type: j.literal("app.bsky.richtext.facet#tag"), tag: j.string }),
  )),
});
export type Facet = j.Infer<typeof facetSchema>;
const validateFacet = j.validation.compile(facetSchema);
const labelsSchema = j.obj({ values: j.array(j.obj({ val: j.string })) });
const validateLabels = j.validation.compile(labelsSchema);
const postSchema = j.obj({
  $type: j.literal("app.bsky.feed.post"),
  text: j.string,
  createdAt: j.string,
  reply: j.optional(j.obj({ root: strongRef, parent: strongRef })),
  facets: j.unknown,
  embed: j.unknown,
  labels: j.unknown,
});
const validatePost = j.validation.compile(postSchema);
export interface PostRecord
  extends Omit<j.Infer<typeof postSchema>, "facets" | "embed" | "labels"> {
  facets?: Facet[];
  embed?: Embed;
  labels?: j.Infer<typeof labelsSchema>;
}

export function parsePost(input: unknown): PostRecord {
  const { value, errors } = validatePost(input);
  if (errors) throw new Error("Invalid post record.");
  // Optional presentation data must not hide otherwise readable text and replies.
  return {
    $type: value.$type,
    text: value.text,
    createdAt: value.createdAt,
    reply: value.reply,
    facets: Array.isArray(value.facets)
      ? value.facets.flatMap((facet) => {
        const result = validateFacet(facet);
        return result.value ? [result.value] : [];
      })
      : undefined,
    embed: validateEmbed(value.embed).value,
    labels: validateLabels(value.labels).value,
  };
}

export const validateProfile = j.validation.compile(j.obj({
  displayName: j.optional(j.string),
  avatar: j.optional(blob),
}));
export const validateDocument = j.validation.compile(j.obj({
  id: j.string,
  alsoKnownAs: j.optional(j.array(j.string)),
  service: j.optional(
    j.array(
      j.obj({ id: j.string, type: j.string, serviceEndpoint: j.unknown }),
    ),
  ),
}));
export const validateRecord = j.validation.compile(
  j.obj({ uri: j.string, value: j.unknown }),
);
export const validateBacklinks = j.validation.compile(j.obj({
  backlinks: j.array(j.obj({ location: j.string, uris: j.array(j.string) })),
  cursor: j.union(j.string, j.null),
}));

export const validateDns = j.validation.compile(j.obj({
  Status: j.number,
  Answer: j.optional(j.array(j.obj({ type: j.number, data: j.string }))),
}));
